'use strict';

/**
 * Global plain-language standard for system-generated explanations.
 * Short sentences, no model jargon, simple punctuation.
 */

const PHRASE_REPLACEMENTS = [
  [/comp-stress pattern(?: on the team)?/gi, 'pay is lower than market across the team'],
  [/compensation stress pattern/gi, 'pay is lower than market'],
  [/exit optionality/gi, 'easier to leave for another job'],
  [/active market pull increases exit optionality\.?/gi, 'Strong outside hiring makes it easier to leave.'],
  [/weighted at ~[\d.]+% of drivers/gi, 'one of the main factors'],
  [/\(about [\d.]+% of the weighted risk signal\)/gi, ''],
  [/weighted risk signal/gi, 'risk factors'],
  [/weighted factor model/gi, 'available HR signals'],
  [/model coverage is [\d.]+%(?:, so additional unmeasured factors may still matter)?\.?/gi, ''],
  [/model coverage/gi, 'data available'],
  [/market benchmark on file/gi, 'market pay level'],
  [/market reference/gi, 'market pay'],
  [/market median/gi, 'market pay'],
  [/on file/gi, 'in our records'],
  [/treat this as an early signal/gi, 'limited HR data — review uses available fields only'],
  [/treat their risk read as an early signal/gi, 'limited HR data — review uses available fields only'],
  [/risk reads are an early signal/gi, 'risk scores use partial profile data'],
  [/early signal/gi, 'limited profile data'],
  [/not enough data yet to fully evaluate/gi, 'limited HR data — directional read from available fields'],
  [/not enough data available/gi, 'limited profile data — see available signals below'],
  [/leave-risk/gi, 'risk of leaving'],
  [/leave risk/gi, 'risk of leaving'],
  [/flight risk/gi, 'risk of leaving'],
  [/offsetting strengths:/gi, 'Strengths that help retention:'],
  [/contributing signals:/gi, 'Other factors:'],
  [/several modeled inputs suggest a closer review is warranted/gi, 'Review this profile for more detail.'],
  [/evaluated factors did not produce a single dominant theme at [\d.]+% model coverage—review the factor breakdown for specifics/gi,
    'No single main driver stands out yet. Review individual factors for more detail.'],
  [/meaningful pay gap in the model/gi, 'meaningful pay gap'],
  [/dominant retention risk signal on file/gi, 'main pay-related risk signal'],
  [/retention signal on file/gi, 'positive retention sign'],
  [/hr signals on file/gi, 'HR records'],
  [/from what we have on file/gi, 'from available data'],
  [/retention signals are broadly stable on file/gi, 'retention looks stable'],
  [/no dominant negative driver on file/gi, 'no main negative driver'],
  [/validate pay, growth path, and manager load this week/gi, 'Review pay, growth path, and manager support this week.'],
];

const TECHNICAL_LINE_RE =
  /\b(?:coverage of the weighted|interaction patterns?\s*\(|raw combined|cap \d|base factor score|base factors|percentFromBase|final score comes from|INSUFFICIENT_MARKET_DATA)\b/i;

function splitSentences(text) {
  return String(text || '')
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function wordCount(sentence) {
  return String(sentence || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function trimSentence(sentence, maxWords = 25) {
  const words = String(sentence || '')
    .trim()
    .replace(/[.!?]+$/, '')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length <= maxWords) {
    const body = words.join(' ');
    return body ? `${body}.` : '';
  }
  return `${words.slice(0, maxWords).join(' ')}.`;
}

function applyPhraseReplacements(text) {
  let out = String(text || '');
  for (const [re, replacement] of PHRASE_REPLACEMENTS) {
    out = out.replace(re, replacement);
  }
  return out;
}

function cleanPunctuation(text) {
  let out = String(text || '');
  out = out.replace(/\s*[—–]\s*/g, '. ');
  out = out.replace(/;\s*/g, '. ');
  out = out.replace(/\(\s*([^()]{0,120})\s*\)/g, (match, inner) => {
    if (/weighted|driver|model|index|coverage|cap|raw combined|\/100/i.test(inner)) return '';
    return match;
  });
  out = out.replace(/\s{2,}/g, ' ');
  out = out.replace(/\.\s*\./g, '.');
  out = out.replace(/,\s*\./g, '.');
  out = out.replace(/\s+([,.!?])/g, '$1');
  return out.trim();
}

/**
 * @param {string} text
 * @param {{ maxWordsPerSentence?: number }} [opts]
 * @returns {string}
 */
function simplifyCopy(text, opts = {}) {
  const maxWords = opts.maxWordsPerSentence ?? 25;
  let out = applyPhraseReplacements(text);
  out = cleanPunctuation(out);
  if (!out) return '';

  const sentences = splitSentences(out).map((s) => trimSentence(s, maxWords));
  out = sentences.filter(Boolean).join(' ');
  if (out && !/[.!?]$/.test(out)) out += '.';
  return out.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Three-part explanation: what / why / next.
 * @param {{ happening?: string, matters?: string, next?: string|null }} parts
 * @returns {string}
 */
function formatStructuredExplanation(parts) {
  const lines = [];
  if (parts.happening) lines.push(simplifyCopy(parts.happening));
  if (parts.matters) lines.push(simplifyCopy(parts.matters));
  if (parts.next) lines.push(simplifyCopy(parts.next));
  return lines.filter(Boolean).join(' ');
}

function limitedDataMessage() {
  return 'Some HR fields are still missing. This summary uses the strongest available signals and may omit factors not yet on file.';
}

function lowCoverageNote(coveragePct) {
  const pct = Math.round(Number(coveragePct) || 0);
  if (pct <= 0) return '';
  if (pct < 40) {
    return `Only ${pct}% of factors had data. Add more HR details for a fuller picture.`;
  }
  return '';
}

/**
 * Final quality gate before user-facing output.
 * @param {string} text
 * @param {{ allowTechnical?: boolean }} [opts]
 * @returns {string}
 */
function finalizeExplanation(text, opts = {}) {
  const simplified = simplifyCopy(text);
  if (!simplified) return '';
  if (opts.allowTechnical || !TECHNICAL_LINE_RE.test(simplified)) {
    return simplified;
  }
  return 'Review this profile for more detail.';
}

const BANNED_PHRASES = [
  'comp-stress',
  'exit optionality',
  'weighted driver',
  'early signal',
  'model coverage',
  'weighted factor',
];

module.exports = {
  simplifyCopy,
  finalizeExplanation,
  formatStructuredExplanation,
  limitedDataMessage,
  lowCoverageNote,
  applyPhraseReplacements,
  cleanPunctuation,
  BANNED_PHRASES,
};
